<template>
  <div class="lp-panel">
    <div class="lp-header">
      <h4 class="lp-title">Launch Post
        <span v-if="slides.length" class="lp-count">({{ slides.length }} slides · {{ captionCount }} captions)</span>
      </h4>
      <span v-if="slides.length" class="lp-meta">Replaces SNS cards for new collections</span>
    </div>

    <div v-if="!hasContent" class="lp-empty">
      <span>Launch post not generated yet.</span>
    </div>

    <template v-else>
      <!-- Slides as visual cards -->
      <div class="lp-section">
        <div class="lp-section-label">Slides</div>
        <div class="lp-slides">
          <div v-for="(slide, i) in slides" :key="'lps-' + i" class="lp-slide-wrap">
            <!-- Card visual -->
            <div class="lp-card" :class="'lp-card--' + (slide.slide_type || 'default')">
              <div class="lp-card-inner">
                <div class="lp-card-bottom-bar">
                  <img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="lp-collection-cover" />
                  <div class="lp-brand-tag">
                    <img src="/LearningFirst_logo.png" class="lp-logo-icon" alt="" /> LearningFirst
                  </div>
                </div>

                <!-- Hook quote / gold quote -->
                <template v-if="slide.slide_type === 'hook_quote' || slide.slide_type === 'gold_quote'">
                  <div class="lp-card-head">
                    <span class="lp-slide-index">{{ i + 1 }}</span>
                    <span class="lp-slide-type">{{ formatType(slide.slide_type) }}</span>
                    <span v-if="slide.gold_quote_ref != null" class="lp-slide-ref">gold_quote #{{ slide.gold_quote_ref }}</span>
                  </div>
                  <div class="lp-card-quote">{{ slide.text }}</div>
                </template>

                <!-- Cover slide -->
                <template v-else-if="slide.slide_type === 'cover'">
                  <div class="lp-card-head">
                    <span class="lp-slide-index">{{ i + 1 }}</span>
                    <span class="lp-slide-type">Cover</span>
                  </div>
                  <div v-if="collectionForm.cover_url" class="lp-card-cover-img">
                    <img :src="collectionForm.cover_url" />
                  </div>
                  <div class="lp-card-cover-title">{{ collectionForm.title }}</div>
                </template>

                <!-- CTA slide -->
                <template v-else-if="slide.slide_type === 'cta'">
                  <div class="lp-card-head">
                    <span class="lp-slide-index">{{ i + 1 }}</span>
                    <span class="lp-slide-type">Cta</span>
                  </div>
                  <div class="lp-card-cta-title">{{ collectionForm.title }}</div>
                  <div v-if="slide.text" class="lp-card-cta-button">{{ slide.text }}</div>
                  <div v-if="slide.url" class="lp-card-cta-url">{{ slide.url }}</div>
                </template>

                <!-- Fallback for unknown types -->
                <template v-else>
                  <div class="lp-card-head">
                    <span class="lp-slide-index">{{ i + 1 }}</span>
                    <span class="lp-slide-type">{{ formatType(slide.slide_type) }}</span>
                  </div>
                  <div v-if="slide.text" class="lp-card-quote">{{ slide.text }}</div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Captions -->
      <div class="lp-section" v-if="captions">
        <div class="lp-section-label">Per-platform captions</div>
        <div class="lp-captions">
          <div v-for="platform in platforms" :key="platform" class="lp-caption-block">
            <div class="lp-caption-head">
              <span class="lp-platform-tag">{{ platform }}</span>
              <span v-if="captionRendered(platform)" class="lp-char-count">{{ captionRendered(platform).length }} chars</span>
              <el-button size="mini" type="text" icon="el-icon-document-copy" @click="copy(captionRendered(platform), platform)">Copy</el-button>
            </div>
            <div v-if="captionRendered(platform)" class="lp-caption-text">{{ captionRendered(platform) }}</div>
            <div v-else class="lp-caption-empty">Not set</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "LaunchPostPanel",
  props: {
    collectionForm: { type: Object, required: true },
    collectionId: { type: String, default: "" },
  },
  data() {
    return {
      platforms: ["x", "instagram", "tiktok", "xiaohongshu", "substack_notes"],
    };
  },
  computed: {
    post() {
      return this.collectionForm && this.collectionForm.launch_post
        ? this.collectionForm.launch_post
        : null;
    },
    hasContent() {
      return !!(this.post && ((this.post.slides && this.post.slides.length) || this.post.captions));
    },
    slides() {
      return (this.post && this.post.slides) || [];
    },
    captions() {
      return this.post && this.post.captions ? this.post.captions : null;
    },
    captionCount() {
      if (!this.captions) return 0;
      return Object.keys(this.captions).length;
    },
  },
  methods: {
    formatType(t) {
      if (!t) return "";
      return t.replace(/_/g, " ");
    },
    captionRendered(platform) {
      if (!this.captions || !this.captions[platform]) return "";
      return this.captions[platform].rendered || "";
    },
    copy(text, label) {
      if (!text) return;
      const fallback = () => {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch (e) {}
        document.body.removeChild(ta);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(`${label} caption copied`))
          .catch(() => { fallback(); this.$message.success(`${label} caption copied`); });
      } else {
        fallback();
        this.$message.success(`${label} caption copied`);
      }
    },
  },
};
</script>

<style scoped>
.lp-panel { padding: 10px 0; }
.lp-header { margin-bottom: 16px; }
.lp-title { font-size: 15px; font-weight: 600; color: #303133; margin: 0; }
.lp-count { font-weight: 400; color: #909399; font-size: 13px; }
.lp-meta { display: block; font-size: 12px; color: #909399; margin-top: 4px; }
.lp-empty { padding: 24px; text-align: center; color: #909399; font-style: italic; }

.lp-section { margin-bottom: 24px; }
.lp-section-label { font-weight: 500; color: #606266; font-size: 13px; margin-bottom: 10px; }

/* Slides row */
.lp-slides { display: flex; gap: 16px; overflow-x: auto; padding-bottom: 8px; }
.lp-slide-wrap { flex-shrink: 0; }

/* Visual card — 5:7 aspect like SNS cards */
.lp-card {
  width: 260px;
  aspect-ratio: 5 / 7;
  container-type: inline-size;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  background: linear-gradient(165deg, #f0f7ff 0%, #ffffff 40%, #f5f9ff 100%);
  border: 1px solid #c3d9f5;
}

.lp-card-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 6% 7% 20% 7%;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}

.lp-card-inner::before {
  content: '';
  position: absolute;
  top: 4%;
  right: -8%;
  width: 50%;
  height: 50%;
  background-image: url('/LearningFirst_logo.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: top right;
  opacity: 0.05;
  pointer-events: none;
  z-index: 0;
}

.lp-card-inner > * {
  position: relative;
  z-index: 1;
}

/* Bottom bar */
.lp-card-bottom-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 14%;
  display: flex;
  align-items: center;
  padding: 0 7%;
  z-index: 2;
  background: linear-gradient(0deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.7) 100%);
}

.lp-collection-cover {
  width: 7cqi;
  height: 9.8cqi;
  object-fit: cover;
  border-radius: 1cqi;
  box-shadow: 0 1px 4px rgba(0,0,0,0.12);
}

.lp-brand-tag {
  font-size: 2.8cqi;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #409EFF;
  display: flex;
  align-items: center;
  gap: 1cqi;
  margin-left: auto;
  line-height: 1;
}

.lp-logo-icon {
  width: 3.6cqi;
  height: 3.6cqi;
  object-fit: contain;
}

/* Card header — index + type badge */
.lp-card-head {
  display: flex;
  align-items: center;
  gap: 1.5cqi;
  margin-bottom: 3cqi;
}

.lp-slide-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 6cqi;
  height: 6cqi;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  font-size: 3cqi;
  font-weight: 600;
  flex-shrink: 0;
}

.lp-slide-type {
  font-size: 3cqi;
  color: #409eff;
  background: #ecf5ff;
  padding: 0.5cqi 2cqi;
  border-radius: 1cqi;
  text-transform: capitalize;
  font-weight: 500;
}

.lp-slide-ref {
  font-size: 2.6cqi;
  color: #909399;
}

/* Quote text */
.lp-card-quote {
  font-size: 4cqi;
  font-weight: 600;
  color: #303133;
  line-height: 1.45;
  flex: 1;
  display: flex;
  align-items: center;
}

/* Cover slide */
.lp-card-cover-img {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 2cqi;
  margin-bottom: 2cqi;
}

.lp-card-cover-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 2cqi;
}

.lp-card-cover-title {
  font-size: 3.5cqi;
  font-weight: 700;
  color: #303133;
  text-align: center;
}

/* CTA slide */
.lp-card-cta-title {
  font-size: 5cqi;
  font-weight: 700;
  color: #303133;
  text-align: center;
  margin-top: auto;
  margin-bottom: 3cqi;
}

.lp-card-cta-button {
  font-size: 3cqi;
  font-weight: 600;
  color: #fff;
  background: #409eff;
  padding: 1.5cqi 4cqi;
  border-radius: 1.5cqi;
  text-align: center;
  align-self: center;
  margin-bottom: 2cqi;
}

.lp-card-cta-url {
  font-size: 2.2cqi;
  color: #909399;
  text-align: center;
  word-break: break-all;
  margin-bottom: auto;
}

/* Captions */
.lp-captions { display: flex; flex-direction: column; gap: 12px; }
.lp-caption-block { border: 1px solid #ebeef5; border-radius: 6px; padding: 12px; }
.lp-caption-head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.lp-platform-tag { display: inline-block; padding: 2px 10px; background: #ecf5ff; color: #409eff; border-radius: 3px; font-size: 12px; font-weight: 500; text-transform: capitalize; }
.lp-char-count { font-size: 11px; color: #909399; }
.lp-caption-text { font-size: 13px; color: #303133; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }
.lp-caption-empty { font-size: 13px; color: #c0c4cc; font-style: italic; }
</style>
